import { Input, Label } from 'components/atoms';
import Button from 'components/atoms/button/Button';
import {
  FormInput,
  InfoBox,
  LabeledStatus,
  TabsNavigation,
} from 'components/molecules';
import { TopBar } from 'components/organisms';
import { TopbarTabs } from 'components/templates';
import { Form, Formik, FormikConfig } from 'formik';
import { useHasPermission } from 'hooks';
import i18next from 'i18next';
import { useTranslation } from 'react-i18next';
import { PiOpenAiLogo } from 'react-icons/pi';
import { useGetIsrc } from 'services/codesDistributor/isrcGenerator';
import {
  useGetGenres,
  useGetLanguages,
  useGetPublisherLightList,
  getGenreLabel,
} from 'services/enums';
import {
  ITrackArtistMutationForm,
  ITrackMutationForm,
  TRACK_CENSORIOUS,
} from 'services/tracks/types';
import { ARTIST_ROLE, STATUS } from 'services/types';
import { BUTTON_VARIANT } from 'types/components/button/button.interface';
import { II18nextPrefix } from 'types/components/components.interface';
import {
  getYearsSelectOptions,
  useEnumToSelectOptions,
} from 'utils/components/inputs/select';
import { getLanguageSelectOptions } from 'utils/global/language.utils';
import {
  conditionalField,
  credensialsValidation,
  externalIdsValidation,
  requiredEnumSelect,
  titlesValidation,
} from 'utils/global/validation.utils';
import { array, boolean, number, object, Schema, string } from 'yup';
import {
  artistsValidation,
  settlementInfoContractValidation,
} from '../music-product-digital/MusicProductDigitalForm';
import { IFormikProps, IFormProps } from '../types';
import TrackArtists from './tabs/TrackArtists';
import TrackContracts from './tabs/TrackContracts';
import TrackContractsFuture from './tabs/TrackContractsFuture';
import TrackContractsHistory from './tabs/TrackContractsHistory';
import TrackExternal from './tabs/TrackExternal';
import TrackFile from './tabs/TrackFile';
import TrackFingerprint from './tabs/TrackFingerprint';
import TrackProductsTab from './tabs/TrackProducts';
import TrackStatistics from './tabs/TrackStatistics';
import TrackText from './tabs/TrackText';
import TrackTitles from './tabs/TrackTitles';

export enum TrackTabs {
  file,
  lyrics,
  artists,
  titles,
  external,
  statistics,
  contracts,
  products,
  fingerprints,
}

export const trackInitialValues: ITrackMutationForm = {
  censorious: TRACK_CENSORIOUS.unknown,
  genre: { id: -1, name: '' },
  language: -1,
  // source: TRACK_SOURCE.unknown,
  status: STATUS.draft,
  isInstrumental: true,
  mainTitle: {
    language: -1,
    title: '',
    version: '',
  },
  cline: { name: '', year: '' },
  pline: { name: '', year: '' },
  text: '',
  isrc: '',
  otherTitles: [],
  artists: [],
  contracts: [],
  externalIds: [],
  statistics: { endSettlementPeriodId: '', startSettlementPeriodId: '' },
  isCreatedWithAi: false,
  hasDuplicatedArtist: null,
  id: '',
  hasMatchFingerprint: false,
  matchFingerprintScore: null,
  waitingForAcoustIdSubmission: false,
  version: 0,
};

const lyricistConditionalValidator = (baseSchema: Schema) =>
  baseSchema
    .when('isInstrumental', {
      is: (instrumental?: boolean) => !instrumental,
      then: schema =>
        schema.test(
          'has-lyricist',
          () => i18next.t('formik-error.track-should-have-lyricist'),
          (artist: ITrackArtistMutationForm[]) =>
            artist && artist.some(item => item.role === ARTIST_ROLE.lyricist),
        ),
      otherwise: schema => schema,
    })
    .when('isInstrumental', {
      is: (instrumental?: boolean) => instrumental,
      then: schema =>
        schema.test(
          'has-not-lyricist',
          () => i18next.t('formik-error.track-should-not-have-lyricist'),
          (artists: ITrackArtistMutationForm[]) =>
            !!!artists.find(item => item.role === ARTIST_ROLE.lyricist),
        ),
      otherwise: schema => schema,
    });

export const trackValidation = object().shape({
  status: requiredEnumSelect(STATUS),
  mainTitle: titlesValidation,
  language: number()
    .not([-1], () => i18next.t('formik-error.required'))
    .required(() => i18next.t('formik-error.required')),
  censorious: conditionalField(requiredEnumSelect(TRACK_CENSORIOUS)),
  genre: conditionalField(
    object().shape({
      id: number()
        .not([-1], () => i18next.t('formik-error.required'))
        .required(() => i18next.t('formik-error.required')),
    }),
  ),
  // source: conditionalField(requiredEnumSelect(TRACK_SOURCE)),
  isrc: conditionalField(
    string().required(() => i18next.t('formik-error.required')),
  ),
  pline: credensialsValidation('status'),
  cline: credensialsValidation('status'),
  isInstrumental: boolean(),
  text: string().nullable(),
  otherTitles: array().of(titlesValidation),
  externalIds: array().of(externalIdsValidation),
  artists: conditionalField(
    lyricistConditionalValidator(
      array()
        .of(artistsValidation)
        .min(1, () => i18next.t('formik-error.at-least-one-artist-required'))
        .test(
          'has-main-artist',
          () =>
            i18next.t('formik-error.what-should-have-main-artist', {
              what: i18next.t('track.what'),
            }),
          artist =>
            artist && artist.some(item => item.role === ARTIST_ROLE.mainArtist),
        )
        .test(
          'has-composer',
          () =>
            i18next.t('formik-error.what-should-have-composer', {
              what: i18next.t('track.what'),
            }),
          artist =>
            artist && artist.some(item => item.role === ARTIST_ROLE.composer),
        ),
    ),
    'status',
    [-1, 1],
    array().of(artistsValidation),
  ),
  file: conditionalField(
    string().when('wavLink', {
      is: (link?: string) => link === undefined,
      then: schema => schema.required(() => i18next.t('formik-error.required')),
      otherwise: schema => schema.optional().nullable(),
    }),
  ),
  wavLink: conditionalField(
    string().when('file', {
      is: (file?: File) => file === undefined,
      then: schema => schema.required(() => i18next.t('formik-error.required')),
      otherwise: schema => schema.optional().nullable(),
    }),
  ),
  contracts: array().of(settlementInfoContractValidation),
});

export interface ITrackForm
  extends FormikConfig<ITrackMutationForm>, IFormProps, II18nextPrefix {}

const TrackForm = ({
  i18nextPrefix,
  topbarContent,
  tittle,
  status,
  initialValues = trackInitialValues,
  disableTabs,
  backPath,
  customBackItem,
  ...props
}: ITrackForm) => {
  const { t } = useTranslation();
  const { getSelectOptions, getStatusOptions } = useEnumToSelectOptions();
  const hasPermission = useHasPermission();
  const getIsrc = useGetIsrc();

  const languages = useGetLanguages();

  const publishers = useGetPublisherLightList();

  const genres = useGetGenres();

  const fetchIsrc = async (
    setFieldValue: (field: string, value: any) => void,
  ) => {
    const result = await getIsrc.refetch();

    if (result?.data) {
      setFieldValue('isrc', result?.data?.value);
    }
  };

  const boolOptions = [
    { value: true, label: t(`enum.LABEL_REPAYMENTS.Yes`) },
    { value: false, label: t(`enum.LABEL_REPAYMENTS.No`) },
  ];

  return (
    <Formik
      {...props}
      validationSchema={trackValidation}
      validateOnChange={true}
      validateOnBlur={true}
      initialStatus={status}
      enableReinitialize
      initialValues={initialValues}>
      {({
        values,
        setFieldValue,
        status,
        errors,
      }: IFormikProps<ITrackMutationForm>) => (
        <Form className='w-full h-full'>
          <TopbarTabs>
            <TopBar
              customBackItem={customBackItem}
              title={status?.displayMode ? tittle : undefined}
              path={backPath ?? 'track'}
              topbarContent={
                <div className='flex w-full justify-end'>
                  {status?.displayMode ? null : (
                    <Input.ComplexWrapper
                      sizes={[50, 50]}
                      className='max-w-[500px]'>
                      <FormInput.Text
                        name='mainTitle.title'
                        label={`${t('input.label.title')}`}
                        autoFocus
                      />
                      <FormInput.Text
                        name='mainTitle.version'
                        label={`${t('input.label.version')}`}
                      />
                    </Input.ComplexWrapper>
                  )}
                  <div className='flex items-center gap-2 justify-end w-full'>
                    {status?.displayMode ? (
                      values.isCreatedWithAi && (
                        <Label color='blue' size='compact'>
                          <PiOpenAiLogo /> {t('input.label.isCreatedWithAi')}
                        </Label>
                      )
                    ) : (
                      <div className='w-48'>
                        <FormInput.Select
                          options={boolOptions}
                          label={t('input.label.isCreatedWithAi')}
                          name='isCreatedWithAi'
                          onChange={option => {
                            setFieldValue(
                              'isCreatedWithAi',
                              option.value ? true : false,
                            );

                            setFieldValue('contracts', []);
                          }}
                        />
                      </div>
                    )}
                    {status?.displayMode ? (
                      <LabeledStatus status={initialValues?.status} />
                    ) : (
                      <div className='w-48'>
                        <FormInput.Select
                          name='status'
                          label={t('input.label.status')}
                          options={getStatusOptions(
                            STATUS,
                            initialValues?.status,
                            'enum.STATUS',
                          )}
                        />
                      </div>
                    )}
                    <div>{topbarContent}</div>
                  </div>
                </div>
              }>
              <InfoBox loading={status?.isLoading}>
                <InfoBox.Items columns={2}>
                  <FormInput.Select
                    name='language'
                    label={t('input.label.language')}
                    options={getLanguageSelectOptions(languages.data, [
                      ...values.otherTitles,
                      values.mainTitle,
                    ])}
                    value={{
                      value: values.mainTitle.language,
                      label: t(`enum.LANGUAGE.${values.mainTitle.language}`),
                    }}
                    onChange={({ value }) =>
                      setFieldValue('mainTitle.language', value)
                    }
                  />

                  <Input.ComplexWrapper
                    sizes={
                      !status?.displayMode &&
                      !(initialValues.isrc || values.isrc) &&
                      hasPermission('CodesDistributorPermissionsLIST**', 8)
                        ? [69.5, 30]
                        : []
                    }
                    className='w-full'>
                    <FormInput.Text
                      name='isrc'
                      label='ISRC'
                      placeholder='USP031900001'
                    />

                    {!status?.displayMode &&
                      !(initialValues.isrc || values.isrc) &&
                      hasPermission('CodesDistributorPermissionsLIST**', 8) && (
                        <Button
                          variant={BUTTON_VARIANT.default}
                          onClick={() => fetchIsrc(setFieldValue)}>
                          <Button.Text text={t('button.generate')} />
                        </Button>
                      )}
                  </Input.ComplexWrapper>

                  <FormInput.Select
                    name='censorious'
                    label={t('input.label.censorious')}
                    options={getSelectOptions(
                      TRACK_CENSORIOUS,
                      'enum.TRACK_CENSORIOUS',
                    )}
                  />

                  <FormInput.Select
                    name='genre.id'
                    label={t('input.label.genre')}
                    options={
                      genres.data?.map(genre => ({
                        value: genre.id,
                        label: getGenreLabel(genre.id, t),
                      })) ?? []
                    }
                    isLoading={genres.isLoading}
                    value={{
                      value: values.genre.id,
                      label: getGenreLabel(values.genre.id, t),
                    }}
                    onChange={option => {
                      if (option) {
                        setFieldValue('genre', {
                          id: option.value,
                          name: getGenreLabel(option.value as number, t),
                        });
                      }
                    }}
                  />
                  <Input.ComplexWrapper sizes={[40, 60]} className='w-full'>
                    <FormInput.Select
                      name='pline.year'
                      label={`${t('input.label.year')}`}
                      placeholder=''
                      options={getYearsSelectOptions()}
                      isCreatable
                    />
                    <FormInput.Select
                      name='pline.name'
                      label={`${t('input.label.pline')}`}
                      placeholder=''
                      options={publishers.data}
                      isCreatable
                      titleEnabled
                    />

                    {/* <FormInput.Text
                      name='pline.name'
                      label={`${t('input.label.pline')}`}
                    /> */}
                  </Input.ComplexWrapper>
                  <Input.ComplexWrapper sizes={[40, 60]} className='w-full'>
                    <FormInput.Select
                      name='cline.year'
                      label={`${t('input.label.year')}`}
                      placeholder=''
                      options={getYearsSelectOptions()}
                      isCreatable
                    />
                    <FormInput.Select
                      name='cline.name'
                      label={`${t('input.label.cline')}`}
                      placeholder=''
                      options={publishers.data}
                      isCreatable
                      titleEnabled
                    />
                  </Input.ComplexWrapper>
                </InfoBox.Items>
              </InfoBox>
              <TabsNavigation
                tabs={[
                  {
                    value: TrackTabs.file,
                    name: 'file',
                    error: errors.file || errors.wavLink,
                  },
                  {
                    value: TrackTabs.lyrics,
                    name: 'lyrics',
                    error: errors.text,
                  },
                  {
                    value: TrackTabs.artists,
                    name: 'artists',
                    error:
                      errors.artists ||
                      (!!values.hasDuplicatedArtist
                        ? t('toast-message.duplicated-artist-error')
                        : undefined),
                  },
                  {
                    value: TrackTabs.titles,
                    name: 'titles',
                    error: errors.otherTitles,
                  },
                  {
                    value: TrackTabs.external,
                    name: 'external',
                    error: errors.externalIds,
                  },
                  {
                    value: TrackTabs.contracts,
                    name: 'contracts',
                    error: errors.contracts,
                    permissionName: 'ContractPermissionsLIST**',
                  },
                  {
                    value: TrackTabs.fingerprints,
                    name: 'fingerprints',
                  },
                  {
                    value: TrackTabs.products,
                    name: 'products',
                  },
                  {
                    value: TrackTabs.statistics,
                    name: 'statistics',
                    permissionName: 'StatisticsPermissionsLIST**',
                  },
                ]}
                i18nextPrefix={i18nextPrefix}
                disabled={disableTabs}
              />
            </TopBar>
          </TopbarTabs>
        </Form>
      )}
    </Formik>
  );
};

TrackForm.getRouterTabs = (disabledChildren?: string[]) =>
  [
    { path: 'lyrics', element: <TrackText /> },
    { path: 'file', element: <TrackFile /> },
    { path: 'artists', element: <TrackArtists /> },
    { path: 'contracts', element: <TrackContracts /> },
    { path: 'contracts/future', element: <TrackContractsFuture /> },
    { path: 'contracts/history', element: <TrackContractsHistory /> },
    { path: 'titles', element: <TrackTitles /> },
    { path: 'external', element: <TrackExternal /> },
    { path: 'products', element: <TrackProductsTab /> },
    { path: 'statistics', element: <TrackStatistics /> },
    { path: 'fingerprints', element: <TrackFingerprint /> },
  ].filter(children => !disabledChildren?.includes(children.path));

export default TrackForm;
